import {
  reduxTypedHooks,
  tSharedAppState,
  lessonSelectors,
  IGameRoundAnswer,
  IPhrase,
  tGameType,
  gameplaysActions
} from 'common/modules/ReactShared';
import { isNaN } from 'lodash';
import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { IMetrics } from './useTaskGamesMetrics';

interface IUseTaskGameplayProps {
  phrases: IPhrase[];
  gameType: tGameType;
  silentMode?: boolean;
  lessonId: number;
  metrics: IMetrics;
  onGameComplete: () => void;
}

interface ITaskGameplay {
  nextGame: () => void;
  roundComplete: (answer: IGameRoundAnswer) => void;
}

const useTaskGameplay = ({
  phrases,
  gameType,
  lessonId,
  silentMode,
  onGameComplete,
  metrics
}: IUseTaskGameplayProps): ITaskGameplay => {
  const lessonGameplay = useSelector((state: tSharedAppState) =>
    lessonSelectors.selectLessonGameplays(state)
  );
  const dispatch = reduxTypedHooks.useTypedDispatch();

  useEffect(() => {
    if (!silentMode && !isNaN(lessonId)) {
      dispatch(gameplaysActions.createLessonGameplay(lessonId));
    }
  }, [lessonId, gameType, dispatch, silentMode]);

  if (silentMode) {
    return {
      nextGame: () => {
        onGameComplete();
      },
      roundComplete: () => {}
    };
  }

  const nextGame = () => {
    const { timeSpent, attempts } = metrics;
    if (!isNaN(lessonId) && lessonGameplay && timeSpent) {
      dispatch(
        gameplaysActions.finishLessonGameplay({
          lessonId,
          gameplayId: lessonGameplay.gameplay.id,
          params: { timeSpent, attempts, xpEarned: 0 }
        })
      );
    }
    metrics.stopTiming();
    onGameComplete?.();
  };

  const roundComplete = (answer: IGameRoundAnswer) => {
    const { id, word, solved } = answer;
    // The answer carries the phrase id; the body lookup only remains as a
    // safety net for stale callers, since bodies are not unique per language.
    const wordId = id ?? phrases?.find((phrase) => phrase.body === word)?.id;
    if (wordId) dispatch(gameplaysActions.playStudentWord({ wordId, solved }));
    if (!solved) metrics.addAttempt();
  };

  return { nextGame, roundComplete };
};

export { useTaskGameplay };
export type { ITaskGameplay, IUseTaskGameplayProps };
